import React, { useState, useEffect } from 'react';
import './Manager.css';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'YOUR_API_ENDPOINT_HERE';

const formatTime = (hours) => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
};

function Manager({ idToken, onBack }) {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [showRateForm, setShowRateForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', firstName: '', lastName: '' });
  const [rateForm, setRateForm] = useState({ startDate: '', rate: '' });
  const [inviteMessage, setInviteMessage] = useState('');
  const [rateMessage, setRateMessage] = useState('');

  useEffect(() => {
    fetchManagerData();
    fetchGroupMembers();
    fetchHourlyRates();
  }, []);

  const fetchManagerData = async () => {
    try {
      const res = await fetch(`${API_ENDPOINT}/manager`, {
        headers: { 'Authorization': idToken }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Access denied or not a manager');
      }
      
      const result = await res.json();
      setData(result);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchGroupMembers = async () => {
    try {
      const res = await fetch(`${API_ENDPOINT}/group-members`, {
        headers: { 'Authorization': idToken }
      });
      if (res.ok) {
        const result = await res.json();
        setMembers(result.members || []);
      }
    } catch (err) {
      console.error('Failed to fetch members:', err);
    }
  };

  const fetchHourlyRates = async () => {
    try {
      const res = await fetch(`${API_ENDPOINT}/hourly-rates`, {
        headers: { 'Authorization': idToken }
      });
      if (res.ok) {
        const result = await res.json();
        setRates(result.rates || []);
      }
    } catch (err) {
      console.error('Failed to fetch rates:', err);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setInviteMessage('');
    setError('');

    try {
      const res = await fetch(`${API_ENDPOINT}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken
        },
        body: JSON.stringify(inviteForm)
      });

      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.message || 'Failed to invite user');
      }

      setInviteMessage(result.message);
      setInviteForm({ email: '', firstName: '', lastName: '' });
      fetchGroupMembers();
      setTimeout(() => {
        setShowInvite(false);
        setInviteMessage('');
      }, 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSetRate = async (e) => {
    e.preventDefault();
    setRateMessage('');
    setError('');

    try {
      const res = await fetch(`${API_ENDPOINT}/hourly-rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken
        },
        body: JSON.stringify(rateForm)
      });

      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.message || 'Failed to set rate');
      }

      setRateMessage('Rate set successfully');
      setRateForm({ startDate: '', rate: '' });
      fetchHourlyRates();
      setTimeout(() => {
        setShowRateForm(false);
        setRateMessage('');
      }, 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRate = async (startDate) => {
    if (!window.confirm('Delete this rate?')) return;

    try {
      const res = await fetch(`${API_ENDPOINT}/hourly-rate`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken
        },
        body: JSON.stringify({ startDate })
      });

      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.message || 'Failed to delete rate');
      }

      fetchHourlyRates();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="manager-container"><p>Loading...</p></div>;
  if (error) return <div className="manager-container"><p className="error">{error}</p><button onClick={onBack}>Back</button></div>;
  if (!data) return null;

  const totalBalance = data?.userBalances?.reduce((sum, u) => sum + u.balance, 0) || 0;
  const totalEarned = data?.userBalances?.reduce((sum, u) => sum + u.totalEntries, 0) || 0;
  const totalPaid = data?.userBalances?.reduce((sum, u) => sum + u.totalPayments, 0) || 0;

  return (
    <div className="manager-container">
      <div className="manager-header">
        <h1>Manager Dashboard - {data?.vehicleId}</h1>
        <button onClick={onBack} className="back-btn">Back to My Dashboard</button>
      </div>

      <div className="dashboard-grid">

        <div className="widget invite-widget">
          <h2>Invite User</h2>
          {!showInvite ? (
            <button onClick={() => setShowInvite(true)} className="invite-btn-large">+ Invite New User</button>
          ) : (
            <div>
              {inviteMessage && <div className="success-msg">{inviteMessage}</div>}
              {error && <div className="error-msg">{error}</div>}
              <form onSubmit={handleInviteUser}>
                <input
                  type="email"
                  placeholder="Email *"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  required
                />
                <input
                  type="text"
                  placeholder="First Name"
                  value={inviteForm.firstName}
                  onChange={(e) => setInviteForm({...inviteForm, firstName: e.target.value})}
                />
                <input
                  type="text"
                  placeholder="Last Name"
                  value={inviteForm.lastName}
                  onChange={(e) => setInviteForm({...inviteForm, lastName: e.target.value})}
                />
                <div className="form-actions">
                  <button type="submit" className="submit-btn">Send</button>
                  <button type="button" onClick={() => setShowInvite(false)} className="cancel-btn">Cancel</button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="widget members-widget">
          <h2>Group Members ({members.length})</h2>
          <div className="members-list">
            {members.map((member, idx) => (
              <div key={idx} className="member-item">
                <div className="member-info">
                  <div className="member-name">{member.name}</div>
                  <div className="member-email">{member.email}</div>
                </div>
                <div className={`member-status ${member.status.toLowerCase()}`}>
                  {member.status === 'FORCE_CHANGE_PASSWORD' ? 'Pending' : member.status}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="widget financial-widget">
          <h2>Financial Overview</h2>
          <div className="financial-summary">
            <div className="financial-item">
              <div className="financial-label">Total Flight Costs</div>
              <div className="financial-value positive">£{totalEarned.toFixed(2)}</div>
            </div>
            <div className="financial-item">
              <div className="financial-label">Total Payments</div>
              <div className="financial-value negative">£{totalPaid.toFixed(2)}</div>
            </div>
            <div className="financial-item total">
              <div className="financial-label">Net Balance</div>
              <div className={`financial-value ${totalBalance >= 0 ? 'positive' : 'negative'}`}>
                £{totalBalance.toFixed(2)}
              </div>
            </div>
          </div>
          <div className="user-balances-summary">
            <h3>User Balances</h3>
            {data?.userBalances?.map((user, idx) => (
              <div key={idx} className="balance-row">
                <span>{user.userName}</span>
                <span className={user.balance >= 0 ? 'positive' : 'negative'}>
                  £{user.balance.toFixed(2)}
                </span>
                {user.lastPaymentStatus && (
                  <span className={`payment-status-badge ${user.lastPaymentStatus}`}>
                    {user.lastPaymentStatus}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="widget rates-widget">
          <h2>Hourly Rates</h2>
          {!showRateForm ? (
            <>
              <button onClick={() => setShowRateForm(true)} className="invite-btn-large">+ Set New Rate</button>
              <div className="rates-list">
                {rates.map((rate, idx) => (
                  <div key={idx} className="rate-item">
                    <div>
                      <span className="rate-date">From {rate.startDate}</span>
                      <span className="rate-value">£{rate.rate}/hr</span>
                    </div>
                    <button onClick={() => handleDeleteRate(rate.startDate)} className="delete-rate-btn">×</button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div>
              {rateMessage && <div className="success-msg">{rateMessage}</div>}
              {error && <div className="error-msg">{error}</div>}
              <form onSubmit={handleSetRate}>
                <input
                  type="date"
                  placeholder="Start Date *"
                  value={rateForm.startDate}
                  onChange={(e) => setRateForm({...rateForm, startDate: e.target.value})}
                  required
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Rate (£/hr) *"
                  value={rateForm.rate}
                  onChange={(e) => setRateForm({...rateForm, rate: e.target.value})}
                  required
                />
                <div className="form-actions">
                  <button type="submit" className="submit-btn">Set Rate</button>
                  <button type="button" onClick={() => setShowRateForm(false)} className="cancel-btn">Cancel</button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Manager;
